import { useCedarStore, useChatInput } from '@/store/CedarStore';
import {
	ChatInput as ChatInputType,
	MultipleChoiceSuggestion,
	defaultSuggestions,
} from '@/store/messagesSlice';
import { JSONContent } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { motion } from 'motion/react';
import { SendHorizontal } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import KeyboardShortcut from '../KeyboardShortcut';
import { ChoiceNode } from './ChoiceNode';

// Define a custom document extension that only allows a single block
const CustomDocument = Document.extend({
	content: 'block',
});

interface ChatInputProps {
	handleFocus: () => void;
	handleBlur: () => void;
	isInputFocused: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
	handleFocus,
	handleBlur,
	isInputFocused,
}) => {
	const nextMessage = useCedarStore((state) => state.nextMessage);
	const setSuggestions = useCedarStore((state) => state.setSuggestions);
	const { overrideInputContent, setChatInputContent, setOverrideInputContent } =
		useChatInput();
	const processingRef = useRef<boolean>(false);
	const latestEditorStateRef = useRef<JSONContent | null>(null);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	const DEBOUNCE_DELAY = 50; // 50ms debounce delay
	const [isEditorEmpty, setIsEditorEmpty] = useState(true);

	// Helper function to process editor state
	const processEditorState = async (editorState: JSONContent) => {
		// Store the latest state for processing
		latestEditorStateRef.current = editorState;

		// Clear any existing debounce timer
		if (debounceTimerRef.current !== null) {
			clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = null;
		}

		// If already processing, don't start another process
		if (processingRef.current) return;

		// Set a debounce timer
		debounceTimerRef.current = setTimeout(async () => {
			// Mark as processing
			processingRef.current = true;

			// Process the latest editor state
			const stateToProcess = latestEditorStateRef.current;
			if (!stateToProcess?.content) {
				// Default case - use default suggestions
				setSuggestions(defaultSuggestions);
				processingRef.current = false;
				return;
			}

			// Find choice nodes in the content
			let firstUnselectedChoice: MultipleChoiceSuggestion | undefined;
			let hasSelectedChoiceNodes = false;

			// One pass through the content to check for both selected and unselected nodes
			for (const node of stateToProcess.content) {
				if (!node.content) continue;

				for (const childNode of node.content) {
					if (childNode.type === 'choice') {
						if (
							!childNode.attrs?.selectedOption ||
							childNode.attrs.selectedOption === ''
						) {
							// Found unselected choice - prioritize this
							firstUnselectedChoice = {
								type: 'multiple_choice_suggestion',
								field: childNode.attrs?.field || '',
								options: childNode.attrs?.options || [],
								chosenValue: childNode.attrs?.selectedOption || '',
								fieldType: childNode.attrs?.fieldType || 'userContext',
							};
							// Exit the loops early since we found what we need
							break;
						} else {
							// Track that we found a selected choice
							hasSelectedChoiceNodes = true;
						}
					}
				}

				// If we found an unselected choice, no need to check more nodes
				if (firstUnselectedChoice) break;
			}

			// Decision logic - prioritize unselected choices first
			if (firstUnselectedChoice) {
				setSuggestions([firstUnselectedChoice]);
			}
			// If we only have selected choices, keep current suggestions
			else if (!hasSelectedChoiceNodes) {
				// Default case - use default suggestions
				setSuggestions([]);
			}

			// Mark as done processing
			processingRef.current = false;
			debounceTimerRef.current = null;
		}, DEBOUNCE_DELAY);
	};

	// Clean up timer on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current !== null) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				// Exclude the default Document and HardBreak to enforce single line
				document: false,
				hardBreak: false,
			}),
			CustomDocument, // Use our custom document
			Placeholder.configure({
				placeholder: 'Ask a question! Tab to start typing',
			}),
			ChoiceNode,
		],
		content: '',
		editable: true,
		onFocus: () => {
			// Call handleFocus to update isInputFocused in parent
			handleFocus();
		},
		onBlur: handleBlur,
		onUpdate: ({ editor }) => {
			const editorState = editor.getJSON();
			setChatInputContent(editorState);

			// Update empty state
			setIsEditorEmpty(editor.isEmpty);

			// Schedule processing with debounce
			processEditorState(editorState);
		},
	});

	// Extract text content including ChoiceNode values
	const getEditorTextWithChoices = () => {
		if (!editor) return '';

		// Get the editor's state
		const { state } = editor;
		const { doc } = state;

		// Initialize result text
		let resultText = '';

		// Process each node in the document
		doc.descendants((node) => {
			// Handle text nodes
			if (node.isText) {
				resultText += node.text;
			}

			// Handle ChoiceNode
			if (node.type.name === 'choice') {
				const attrs = node.attrs;
				// const field = attrs.field || '';
				const options = attrs.options || [];
				const selectedOption = attrs.selectedOption || '';

				// Use selectedOption if available, otherwise use the first option
				const optionValue =
					selectedOption || (options.length > 0 ? options[0] : '');

				// Add the field and selected option to the result text
				resultText += ` ${optionValue} `;
			}

			return true;
		});

		return resultText;
	};

	// Use overrideInputContent to update the editor
	useEffect(() => {
		if (!editor || !overrideInputContent?.input) return;

		setIsEditorEmpty(false);
		convertoverrideInputContentToEditor(overrideInputContent.input);
	}, [editor, overrideInputContent]);

	// Convert overrideInputContent to editor content
	const convertoverrideInputContentToEditor = (
		input: string | ChatInputType[] | null
	) => {
		if (!editor || !input) return;

		// Clear the editor content
		editor.commands.clearContent();

		// If overrideInputContent is a string, set it as plain text
		if (typeof input === 'string') {
			editor.commands.setContent(input);
			return;
		}

		// If overrideInputContent is an array of ChatInput, insert each item
		if (Array.isArray(input)) {
			// Insert each input item
			input.forEach((item) => {
				if (typeof item === 'string') {
					// Insert text
					editor.commands.insertContent(item);
				} else {
					// Insert choice node
					editor.commands.setChoice({
						field: item.field,
						options: item.options,
						selectedOption: item.chosenValue,
					});
				}
			});
		}
	};

	// Handle custom submit
	const handleSubmit = async () => {
		if (!editor || isEditorEmpty) return;

		const textContent = getEditorTextWithChoices();

		// Only submit if there's content
		if (textContent.trim()) {
			// Call the parent's handleSubmit
			nextMessage(textContent);
			processEditorState(editor.getJSON());

			editor.commands.clearContent();
			setOverrideInputContent('');

			// Clear the overrideInputContent state
			setChatInputContent({
				type: 'doc',
				content: [{ type: 'paragraph', content: [] }],
			});

			editor.commands.focus();
			handleFocus();
		}
	};

	// Handle keyboard events
	const handleKeyDown = (e: React.KeyboardEvent) => {
		// Enter without Shift submits the form
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSubmit();
		}
	};

	// Focus the editor when isInputFocused changes to true
	useEffect(() => {
		if (isInputFocused && editor) {
			editor.commands.focus();
		}
	}, [isInputFocused, editor]);

	return (
		<div className='relative w-full'>
			<div className='flex items-center px-2 text-sm'>
				{editor && !editor.isFocused && (
					<div className='flex items-center flex-shrink-0 mr-2'>
						<KeyboardShortcut shortcut='Tab' />
					</div>
				)}
				<motion.div
					layoutId='chatInput'
					id='cedar-chat-input'
					className='flex-1 justify-center py-2'
					onKeyDown={handleKeyDown}
					aria-label='Message input'>
					<EditorContent
						editor={editor}
						className='prose prose-sm max-w-none focus:outline-none outline-none focus:ring-0 ring-0 [&_*]:focus:outline-none [&_*]:outline-none [&_*]:focus:ring-0 [&_*]:ring-0 text-gray-900 placeholder-gray-500 [&_.ProseMirror]:p-0 [&_.ProseMirror]:outline-none [&_.ProseMirror]:empty:before:content-[attr(data-placeholder)] [&_.ProseMirror]:empty:before:text-gray-400 [&_.ProseMirror]:empty:before:float-left [&_.ProseMirror]:empty:before:pointer-events-none'
					/>
				</motion.div>

				<motion.button
					layoutId='send-chat'
					onClick={handleSubmit}
					disabled={isEditorEmpty}
					style={{
						opacity: isEditorEmpty ? 0.5 : 1,
					}}
					className='flex items-center flex-shrink-0 rounded-md ml-2 p-1 text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:hover:bg-transparent'
					aria-label='Send message'>
					<KeyboardShortcut shortcut='Enter' className='mr-1' />
					<SendHorizontal className='w-4 h-4' />
				</motion.button>
			</div>
		</div>
	);
};
